// Bare-body non-app dealer locations list for the Manufacturing →
// Plants dealers-attached box. Full CRUD on mfg_dealer_locations
// scoped to the selected plant (or the Unattached pseudo-row's set).
//
// Slice 8: no longer renders its own SectionCard — the parent
// (ManufacturingPlantDealersAttachedBox) collapsed the outer/middle/
// inner triple-card nest into a single tabbed section, and this body
// now sits directly inside it. The list count moved into the tab
// label; description / title chrome belongs to the parent.
//
// Same UX pattern as the carriers + plants lists — single locked-row
// editor at a time, soft-delete via Deactivate, no hard delete in v1.
// Detach clears primary_plant_id without deactivating the location.
import { Fragment } from "react";
import { ghostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingPlantDealersLocationForm } from "./ManufacturingPlantDealersLocationForm";
import type { UseManufacturingPlantDealersTab } from "./useManufacturingPlantDealersTab";

export function ManufacturingPlantDealersLocationsSection({
  m,
}: {
  m: UseManufacturingPlantDealersTab;
}) {
  const list = m.scopedLocations;

  return (
    <div className="space-y-3">
      {m.creatingLocation ? (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/30 p-3 dark:border-indigo-800 dark:bg-indigo-950/15">
          <ManufacturingPlantDealersLocationForm
            draft={m.locationCreateDraft}
            setDraft={m.setLocationCreateDraft}
            m={m}
          />
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              onClick={() => void m.submitCreateLocation()}
              disabled={m.actionBusy}
            >
              {m.actionBusy ? "Saving…" : "Create dealer location"}
            </button>
            <button type="button" className={ghostBtnClass} onClick={m.cancelCreateLocation}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {m.listBusy ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading locations…</div>
      ) : null}
      {!m.listBusy && list.length === 0 && !m.creatingLocation ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          {m.isUnattached
            ? "No unattached non-app locations — every location in this org has a primary plant set."
            : "No non-app locations attached to this plant yet."}
        </div>
      ) : null}

      <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
        {list.map((loc) => {
          const isEditing = m.editingLocationId === loc.id;
          const plantLabel = loc.primary_plant_id
            ? m.plantNameById.get(loc.primary_plant_id) ?? loc.primary_plant_id.slice(0, 8)
            : "(none)";
          const linkedDealer = loc.linked_dealer_org_id
            ? m.subscriberDealers.find((s) => s.dealer_org_id === loc.linked_dealer_org_id)
                ?.dealer_org_name
            : null;
          const addressLine = [
            (loc.address as Record<string, unknown>)?.street,
            (loc.address as Record<string, unknown>)?.city,
            (loc.address as Record<string, unknown>)?.state,
            (loc.address as Record<string, unknown>)?.postal_code,
          ]
            .filter((x) => typeof x === "string" && x)
            .join(", ");
          return (
            <li key={loc.id} className="py-3">
              {isEditing ? (
                <Fragment>
                  <ManufacturingPlantDealersLocationForm
                    draft={m.locationEditDraft}
                    setDraft={m.setLocationEditDraft}
                    m={m}
                  />
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      className={mfgPrimaryBtnClass}
                      onClick={() => void m.submitEditLocation()}
                      disabled={m.actionBusy}
                    >
                      {m.actionBusy ? "Saving…" : "Save"}
                    </button>
                    <button type="button" className={ghostBtnClass} onClick={m.cancelEditLocation}>
                      Cancel
                    </button>
                  </div>
                </Fragment>
              ) : (
                <div className="flex flex-wrap items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                      {loc.name}
                      {!loc.is_active ? (
                        <span className="ml-2 rounded-md bg-gray-400 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                          Inactive
                        </span>
                      ) : null}
                      {linkedDealer ? (
                        <span className="ml-2 rounded-md bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                          Linked: {linkedDealer}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-xs text-gray-600 dark:text-zinc-400 space-y-0.5">
                      <div>Primary plant: {plantLabel}</div>
                      {addressLine ? <div>{addressLine}</div> : null}
                      {loc.contact_name || loc.contact_phone || loc.contact_email ? (
                        <div>
                          Contact:{" "}
                          {[loc.contact_name, loc.contact_phone, loc.contact_email]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      ) : null}
                      {loc.notes ? <div className="italic">{loc.notes}</div> : null}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      className={ghostBtnClass}
                      onClick={() => m.startEditLocation(loc)}
                      disabled={m.actionBusy}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={ghostBtnClass}
                      onClick={() => void m.toggleLocationActive(loc, !loc.is_active)}
                      disabled={m.actionBusy}
                    >
                      {loc.is_active ? "Deactivate" : "Reactivate"}
                    </button>
                    {loc.primary_plant_id ? (
                      <button
                        type="button"
                        className={ghostBtnClass}
                        onClick={() => void m.detachLocation(loc)}
                        disabled={m.actionBusy}
                        title="Clear primary plant on this location (location row preserved)"
                      >
                        Detach
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
